import React, { useEffect, useRef, useState } from 'react';

import moment from 'moment';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { CalendarAssignmentEmployee } from 'types/interfaces';
import { PickerTexts } from 'utils';

import { CustomDropdown } from 'ui/custom-controls';
import { TagsComponent } from 'ui/TagsComponent';

interface PlanningParamsProps {
  selectedDateFilter: Date | null;
  mode?: 'planning' | 'deletion' | 'absence_deletion';
  employeesOptions: CalendarAssignmentEmployee[];
  onCancel: () => void;
  onConfirm: (
    startDate: Date,
    endDate: Date,
    selectedEmployees: CalendarAssignmentEmployee[],
    shouldClearBeforeRecalc?: boolean,
    shouldDeleteAbsences?: boolean
  ) => void;
}

const PlanningParamsCard: React.FC<PlanningParamsProps> = ({
  selectedDateFilter,
  mode = 'planning',
  employeesOptions,
  onCancel,
  onConfirm
}) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedEmployees, setSelectedEmployees] = useState<
    CalendarAssignmentEmployee[]
  >([]);
  const [shouldClearBeforeRecalc, setShouldClearBeforeRecalc] = useState(true);
  const [shouldDeleteAbsences, setShouldDeleteAbsences] = useState(true);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    if (selectedDateFilter) {
      const selectedMonth = new Date(selectedDateFilter);
      const isSelectedCurrentMonth =
        selectedMonth.getMonth() === new Date().getMonth();
      setStartDate(
        isSelectedCurrentMonth
          ? selectedMonth
          : new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1)
      );
      setEndDate(
        new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0)
      );
      if (mode === 'deletion' || mode === 'absence_deletion') {
        setSelectedEmployees([]);
      } else {
        setSelectedEmployees(employeesOptions);
      }
    }
  }, [selectedDateFilter, mode]);

  const handleConfirm = () => {
    if (!startDate || !endDate || selectedEmployees.length === 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Заполните все обязательные параметры',
        life: 5000
      });
      return;
    }

    if (mode === 'planning') {
      onConfirm(
        startDate,
        endDate,
        selectedEmployees,
        shouldClearBeforeRecalc,
        shouldDeleteAbsences
      );
    } else {
      onConfirm(startDate, endDate, selectedEmployees);
    }
  };

  const handleDeleteTag = (employeeId: string | number) => {
    setSelectedEmployees((prev) => prev.filter((e) => e.id !== employeeId));
  };

  return (
    <div className="planningParams-card dialog-tabless-card flex flex-column gap-3">
      <Toast ref={toast} />
      <div className="flex flex-row align-items-center gap-2">
        <label htmlFor="startDate" style={{ width: '120px' }}>
          Дата начала<span className="required">*</span>:
        </label>
        <Calendar
          id="startDate"
          value={startDate}
          onChange={(e) => setStartDate(e.value as Date)}
          dateFormat="dd.mm.yy"
          nextIcon="none"
          prevIcon="none"
          showIcon
          minDate={
            selectedDateFilter
              ? moment(selectedDateFilter).startOf('month').toDate()
              : undefined
          }
          maxDate={
            selectedDateFilter
              ? moment(selectedDateFilter).endOf('month').toDate()
              : undefined
          }
          className="flex-grow-1 flex-shrink-1 planning-params-field-row-calendar"
        />
      </div>

      <div className="flex flex-row align-items-center gap-2">
        <label htmlFor="endDate" style={{ width: '120px' }}>
          Дата завершения<span className="required">*</span>:
        </label>
        <Calendar
          id="endDate"
          value={endDate}
          onChange={(e) => setEndDate(e.value as Date)}
          dateFormat="dd.mm.yy"
          nextIcon="none"
          prevIcon="none"
          showIcon
          minDate={
            startDate ||
            (selectedDateFilter
              ? moment(selectedDateFilter).startOf('month').toDate()
              : undefined)
          }
          maxDate={
            selectedDateFilter
              ? moment(selectedDateFilter).endOf('month').toDate()
              : undefined
          }
          className="flex-grow-1 flex-shrink-1 planning-params-field-row-calendar"
        />
      </div>
      <div className="flex flex-column mb-2">
        <div className="flex flex-row align-items-center gap-2 flex-shrink-0">
          <label style={{ width: '120px' }}>
            Сотрудники<span className="required">*</span>:
          </label>
          <CustomDropdown<CalendarAssignmentEmployee>
            multiplySelectMode
            assetOptions={employeesOptions}
            selectedAsset={selectedEmployees}
            onSelectionChange={(e) =>
              setSelectedEmployees(e as CalendarAssignmentEmployee[])
            }
            placeholder={PickerTexts.employees(selectedEmployees.length)}
            passedClassName="flex-grow-1 flex-shrink-1 min-w-0 planning-params-employee-dropdown"
            fieldName="name"
            emptyMessage="Нет доступных сотрудников."
            showClearOnHover
            showAll
            selectAllBtn
          />
        </div>

        <Tooltip target=".employee-tags-card" position="bottom" />
        <div
          className="employee-tags-card executor-tags-container planning-params-employee-tags grid flex flex-column mt-3"
          style={{
            overflow: 'hidden',
            maxHeight: '234px',
            minHeight: '50px',
            marginRight: '0px',
            padding: '5px 0.5rem 3rem 0.65rem',
            borderRadius: '6px'
          }}
          data-pr-tooltip="Выбранные сотрудники"
        >
          <div className="flex flex-column">
            <div className="grid flex justify-content-between align-items-center">
              <label style={{ width: '130px' }} htmlFor="priority"></label>
              {selectedEmployees?.length > 0 && (
                <div
                  style={{
                    width: '100%',
                    maxHeight: '234px',
                    overflow: 'auto',
                    paddingTop: '5px'
                  }}
                  id="tags-scroll"
                >
                  <TagsComponent
                    initialTagsItems={selectedEmployees.map((emp) => ({
                      key: emp.id,
                      label: emp.name
                    }))}
                    onDelete={handleDeleteTag}
                  />
                </div>
              )}
              {selectedEmployees?.length < 1 && (
                <div
                  style={{
                    width: '100%',
                    maxHeight: '84px',
                    overflow: 'auto'
                  }}
                  id="tags-scroll"
                >
                  Сотрудники не выбраны
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {mode === 'planning' && (
        <>
          <div className="flex flex-row align-items-center gap-2">
            <Checkbox
              inputId="clearBefore"
              checked={shouldClearBeforeRecalc}
              onChange={(e) => setShouldClearBeforeRecalc(e.checked!)}
            />
            <label htmlFor="clearBefore">
              Очищать график перед планированием
            </label>
          </div>
          <div className="flex flex-row align-items-center gap-2">
            <Checkbox
              inputId="deleteAbsences"
              checked={shouldDeleteAbsences}
              onChange={(e) => setShouldDeleteAbsences(e.checked!)}
            />
            <label htmlFor="deleteAbsences">Удалять отсутствия</label>
          </div>
        </>
      )}

      <div className="planningParams-card__buttons default-card__buttons">
        <div className="flex gap-3">
          <Button
            label={mode === 'planning' ? 'Запланировать' : 'Удалить'}
            className={
              mode === 'deletion' ? 'p-button-danger' : 'p-button-primary'
            }
            onClick={handleConfirm}
          />
          <Button
            label="Отмена"
            className="p-button-secondary"
            onClick={onCancel}
          />
        </div>
      </div>
    </div>
  );
};

export default PlanningParamsCard;
